<template>
    <div class="cart-discounts-list" :class="{ disabled: isSaving }">
        <div v-if="isLoading" class="loading-placeholder">
            <div class="loading">
                <div class="loader green"></div>
                {{ translations.general.loading }}
            </div>
        </div>

        <template v-if="!isLoading">
            <h4 v-if="itemLevelDiscounts.length > 0">{{ translations.billDiscountsList.itemLevelDiscounts }}</h4>
            <div
                v-for="discount in itemLevelDiscounts"
                class="discount-item"
                :class="{ removable: true }"
                :key="discount.discountId"
                @click="removeItemLevelDiscount(discount)"
            >
                <span>{{ translate(discount.discountName) }}</span>
                <span class="applied-amount">{{ toMoneyWithSign(discount.appliedAmount) }}</span>
                <span class="remove-btn"><i class="fas fa-trash"></i></span>
            </div>

            <h4 v-if="discounts.length > 0">{{ translations.billDiscountsList.orderOrBillLevelDiscounts }}</h4>
            <div
                v-for="discount in discounts"
                class="discount-item"
                :class="{ removable: true }"
                :key="discount.id"
                @click="removeDiscount(discount)"
            >
                <span>{{ translate(discount.discountName) }}</span>
                <span class="applied-amount">{{ toMoneyWithSign(discount.amount) }}</span>
                <span class="remove-btn"><i class="fas fa-trash"></i></span>
            </div>

            <h4 v-if="vouchers.length > 0">{{ translations.billDiscountsList.vouchers }}</h4>
            <div
                v-for="voucher in vouchers"
                class="discount-item"
                :class="{ removable: true }"
                :key="voucher.id"
                @click="removeVoucher(voucher)"
            >
                <span>{{
                    voucher.voucherCode?.substring(0, 4) +
                    "..." +
                    voucher.voucherCode?.substring(voucher.voucherCode.length - 4)
                }}</span>
                <span class="applied-amount">{{ toMoneyWithSign(voucher.amount) }}</span>
                <span class="remove-btn"><i class="fas fa-trash"></i></span>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type {
        ICartDiscount,
        ICartDiscountsSummary,
        ICartItemDiscountSummary,
        ICustomerCart,
    } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, onMounted, ref, watch } from "vue";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const cart = useCartStore();

    const emit = defineEmits(["discountRemoved", "allDiscountsRemoved"]);

    const isSaving = ref(false);
    const isLoading = ref(true);
    const cartDiscountsSummary = ref<ICartDiscountsSummary | null>(null);

    const itemLevelDiscounts = computed(() => cartDiscountsSummary.value?.appliedItemDiscountSummaries ?? []);
    const discounts = computed(() =>
        (cartDiscountsSummary.value?.appliedDiscounts ?? []).sort(
            (a, b) => a.createdDateTime.getTime() - b.createdDateTime.getTime()
        )
    );
    const vouchers = computed(() =>
        (cartDiscountsSummary.value?.appliedVouchers ?? []).sort(
            (a, b) => a.createdDateTime.getTime() - b.createdDateTime.getTime()
        )
    );

    const isAnyDiscountVisible = computed(
        () => itemLevelDiscounts.value.length > 0 || discounts.value.length > 0 || vouchers.value.length > 0
    );

    // define html refs
    async function removeItemLevelDiscount(discount: ICartItemDiscountSummary) {
        removeSelectedDiscount(() => customerDao.removeCartItemDiscount(cart.currentCart!.cartId, discount.discountId));
    }

    async function removeDiscount(discount: ICartDiscount) {
        removeSelectedDiscount(() => customerDao.removeCartDiscount(cart.currentCart!.cartId, discount.discountId!));
    }

    async function removeVoucher(voucher: ICartDiscount) {
        removeSelectedDiscount(() => customerDao.removeCartVoucher(cart.currentCart!.cartId, voucher.voucherId!), true);
    }

    async function removeSelectedDiscount(cartRemoveAction: () => Promise<ICustomerCart>, isVoucher: boolean = false) {
        const message = isVoucher
            ? translations.discounts.removeVoucherPrompt
            : translations.discounts.removeDiscountPrompt;

        Popup.show(
            {
                message: message,
                confirmHandler: async () => {
                    isSaving.value = true;

                    try {
                        const currentCart = await cartRemoveAction();
                        cart.setCurrentCart(currentCart);
                        emit("discountRemoved");
                    } catch (ex) {
                        logError(ex);
                        handleError();
                    } finally {
                        isSaving.value = false;
                    }
                },
                confirmButton: translations.general.remove,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: true,
                confirmBtnOnTheLeft: false,
                uniqueId: "confirmCartDiscountRemovalPopup",
                containerClass: "confirm-cart-discount-removal-popup",
            },
            $("body")
        );
    }

    async function loadCartDiscountsSummary() {
        try {
            isLoading.value = true;
            cartDiscountsSummary.value = await customerDao.getCartDiscountsSummary();

            if (!isAnyDiscountVisible.value) {
                emit("allDiscountsRemoved");
            }
        } catch (error) {
            logError(error);
            handleError();
        } finally {
            isLoading.value = false;
        }
    }

    function handleError() {
        Popup.show(
            {
                containerClass: "remove-cart-discount-error-popup",
                title: translations.general.error,
                message: translations.general.operationFailed,
            },
            $("body")
        );
    }

    onMounted(() => {
        loadCartDiscountsSummary();
    });

    watch(
        () => cart.currentCart,
        () => {
            loadCartDiscountsSummary();
        }
    );
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .cart-discounts-list {
        display: table;

        h4 {
            margin-top: 1em;
            margin-bottom: 0.5em;
            font-weight: 500;
            line-height: 1.2;

            &:first-child {
                margin-top: 0;
            }
        }

        .discount-item {
            display: table-row;
            transition: opacity 0.3s variables.$easeOutExpo;

            &.removable {
                cursor: pointer;

                &:hover {
                    opacity: 0.5;
                }
            }

            > span {
                display: table-cell;
                &:first-child {
                    width: 100%;
                }

                i {
                    padding: 7px;
                }

                &.applied-amount {
                    text-align: right;
                }
            }

            .remove-btn {
                color: #484848;
                padding-left: 10px;
            }

            &.disabled {
                cursor: default;
                opacity: 0.5;

                &.removable {
                    cursor: default;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }
    }

    .confirm-cart-discount-removal-popup {
        z-index: 1510;
    }

    .remove-cart-discount-error-popup {
        z-index: 1511;
    }
</style>
